import type {RichTextField} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {AnimatePresence, motion} from 'framer-motion'
import Link from 'next/link'
import {linkResolver} from 'prismicio'
import {useCallback, useRef, useState} from 'react'
import {ButtonV2} from 'src/common/ButtonV2'
import {Icon} from 'src/common/Icon'
import {EMAIL_CAPTURE_FADE_VARIANTS, EMAIL_CAPTURE_TRANSITION} from './transitions'
import {useHubspotEmailSubmit} from './useHubspotEmailSubmit'

export type EmailCaptureFieldProps = {
  placeholder: string
  submitButtonLabel: string
  successMessage: string
  genericError: string
  checkboxLabel: RichTextField
  hubspotPortalId: string
  hubspotFormId: string
  pageUid: string
}

export const EmailCaptureField = ({
  placeholder,
  submitButtonLabel,
  successMessage,
  genericError,
  checkboxLabel,
  hubspotPortalId,
  hubspotFormId,
  pageUid,
}: EmailCaptureFieldProps) => {
  const [email, setEmail] = useState('')
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false)
  const [showAgreementError, setShowAgreementError] = useState(false)
  const checkboxRef = useRef<HTMLInputElement>(null)
  const {status, submitEmail} = useHubspotEmailSubmit({hubspotPortalId, hubspotFormId, pageUid})

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      if (!hasAgreedToTerms) {
        setShowAgreementError(true)
        checkboxRef.current?.reportValidity()
        return
      }

      setShowAgreementError(false)
      submitEmail(email)
    },
    [email, hasAgreedToTerms, submitEmail],
  )

  return (
    <AnimatePresence mode="wait" initial={false}>
      {status === 'success' ? (
        <motion.div
          key="success"
          {...EMAIL_CAPTURE_FADE_VARIANTS}
          transition={EMAIL_CAPTURE_TRANSITION}
          className="flex w-full max-w-[380px] flex-col items-center gap-1 rounded-lg bg-moss-white/[7%] px-5 py-6 text-center"
        >
          <Icon className="size-12 text-green-neon" name="filled-check-v2" />
          <p className="text-tg-body-l text-moss-white">{successMessage}</p>
        </motion.div>
      ) : (
        <motion.div
          key="form"
          {...EMAIL_CAPTURE_FADE_VARIANTS}
          transition={EMAIL_CAPTURE_TRANSITION}
          className="relative flex flex-col items-center gap-3"
        >
          <form
            onSubmit={handleSubmit}
            noValidate
            className="flex items-center justify-center gap-[4px] rounded-[6px] border border-moss-white/10 bg-moss-white p-[10px] pl-[14px] backdrop-blur-[10px]"
          >
            <div className="flex min-h-px min-w-px grow items-center gap-[2px]">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={placeholder}
                required
                className="text-body-bold w-full bg-transparent text-black placeholder:text-moss-grey-300 focus:outline-none"
              />
            </div>
            <ButtonV2
              variant="primary"
              type="submit"
              disabled={status === 'loading'}
              className="shrink-0 text-nowrap uppercase disabled:cursor-not-allowed"
              trackingData={{
                location: 'blob-section-email-capture',
                dataType: 'submit-button',
                pageName: pageUid,
                label: submitButtonLabel,
              }}
            >
              {submitButtonLabel}
            </ButtonV2>
          </form>
          <label className="flex cursor-pointer items-center gap-3">
            <div className="relative mt-0.5 grid size-4 shrink-0">
              <input
                ref={checkboxRef}
                type="checkbox"
                checked={hasAgreedToTerms}
                required
                onChange={(e) => {
                  setHasAgreedToTerms(e.target.checked)

                  if (e.target.checked) {
                    setShowAgreementError(false)
                  }
                }}
                className="peer sr-only"
              />
              <div
                className={cn(
                  'rounded-4 border bg-transparent transition-colors span-full peer-checked:border-moss-white peer-checked:bg-moss-white',
                  showAgreementError ? 'border-ui-red-450' : 'border-moss-white/40',
                )}
              />
              <Icon
                className="size-4 text-green-dark opacity-0 transition-opacity span-full peer-checked:opacity-100"
                name="tick"
              />
            </div>
            <PrismicRichText
              field={checkboxLabel}
              components={{
                paragraph: ({children}) => <p className="text-tg-body-s text-moss-white">{children}</p>,
                hyperlink: ({node, children}) => (
                  <Link className="text-moss-white/60 underline underline-offset-2" href={linkResolver(node.data)}>
                    {children}
                  </Link>
                ),
              }}
            />
          </label>
          {!showAgreementError && status === 'error' && (
            <p className="text-tg-body-s absolute -bottom-6 text-ui-red-450">{genericError}</p>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
